
import React, { useEffect, useState } from "react";
import { Box, Button, DialogActions, TextField, Typography, useTheme } from "@mui/material";
import ReactApexChart from 'react-apexcharts';
import useApi from "../../../shared/hooks/useApi";
import LoadingScreen from "../../../shared/components/LoadingScreen/LoadingScreen";

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import "./predictionChart.css";

export default function SetpointPredictionChart({ from, to, setLoading }) {
    const { apiGet } = useApi();
    const [finalReducedData, setFinalReducedData] = useState({});
    const [chartData, setChartData] = useState({});

    const setpoints = ["W95_BO1_input", "W95_setpoint", "W55_setpoint", "W7_setpoint"]
    const [selectedSetpoints, setSelectedSetpoints] = useState(["W95_setpoint"]);
    const [sensitivity, setSensitivity] = useState(1);

    const [allPredsNames, setAllPredsNames] = useState([]);
    const [openOptionList, setOpenOptionList] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [spotPrices, setSpotPrices] = useState([]);
    const [savedTooltipsSetpoints, setSavedTooltipsSetpoints] = useState([]);

    const getTooltipClass = (htmlContent) => {
        const match = htmlContent.match(/class="([^"]*)/);

        return match ? match[1] : '';
    };
    // Modify the event listener to add new tooltips
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key.toLowerCase() === 's') {

                const tooltipElement = document.querySelector('.custom-tooltip2');


                if (tooltipElement) {
                    const tooltipClass = getTooltipClass(tooltipElement.outerHTML);
                    if (tooltipClass === "custom-tooltip2") {
                        // add check if tooltip already exists
                        const tooltipExists = savedTooltipsSetpoints.some(tooltip => tooltip.content === tooltipElement.outerHTML);
                        if (!tooltipExists) {
                            setSavedTooltipsSetpoints(prev => [{
                                id: Date.now(),
                                content: tooltipElement.outerHTML,
                                backgroundColor: "#e1e2fe",
                                timestamp: new Date() // Add timestamp for sorting
                            }, ...prev]); // Add new tooltip at the beginning
                        }
                    }
                }
            }
        };

        window.addEventListener('keypress', handleKeyPress);
        return () => window.removeEventListener('keypress', handleKeyPress);
    }, [savedTooltipsSetpoints]);

    const handleRemoveTooltip = (tooltipId) => {
        setSavedTooltipsSetpoints(prev => prev.filter(tooltip => tooltip.id !== tooltipId));
    };

    const generateNameFromDate = (dateString, setpointName) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const shortSetpointName = setpointName.split("_")[0];
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${shortSetpointName} - ${day}th ${hours}:${minutes}`;
    }
    const reduceData = (data, sensitivity) => {
        // Get current time minus one hour as reference
        const referenceTime = new Date();
        referenceTime.setHours(referenceTime.getHours() - sensitivity);
        const processedData = {};
        // Process each setpoint
        Object.entries(data).forEach(([setpointName, predictions]) => {
            processedData[setpointName] = {};
            const predictionsByHour = {};
            // Process each prediction timestamp
            Object.entries(predictions).forEach(([predictionTime, values]) => {
                const predictionDateTime = new Date(predictionTime);
                const filteredValues = []
                if (predictionDateTime >= referenceTime) {
                    filteredValues.push(...values)
                } else {
                    const hourKey = predictionDateTime.toISOString().slice(0, 13); // Get YYYY-MM-DDTHH
                    if (!predictionsByHour[hourKey]) {
                        predictionsByHour[hourKey] = true;
                        let newValues = values.map(value => ({
                            ...value,
                            value: value.value - 30 + parseInt(Math.random() * 10)
                        }));
                        filteredValues.push(...values)
                        //filteredValues.push(...newValues)
                    }
                }
                const name = generateNameFromDate(predictionTime, setpointName);
                processedData[setpointName][name] = filteredValues;
                if (filteredValues.length > 0 && allPredsNames.length === 0) {
                    setAllPredsNames(prev => [...prev, name]);
                    setSelectedOptions(prev => [...prev, name]);
                }
            });
        });
        return processedData;
    };
    useEffect(() => {
        setLoading(true);
        const getPredictions = async () => {
            try {
                const fromDate = from.toISOString();
                const toDate = to.toISOString();
                const source = "setpoint_predictions";
                const getDataRoute = `/esid-24-003/get-setpoint-predictions?from=${fromDate}&to=${toDate}&setpoints=${selectedSetpoints}&source=${source}`;
                const data = await apiGet("UX_HTTP_API", getDataRoute);
                const reducedData = reduceData(data, sensitivity);
                const getSpotPricesRoute = "/common/market-data?from=" + fromDate + "&to=" + toDate + "&data=electricity_day_ahead_prices&version=1";

                const getSpotPrices = async () => {
                    const spotPrices = await apiGet("UX_HTTP_API", getSpotPricesRoute);
                    setSpotPrices(spotPrices);
                };
                getSpotPrices();
                setFinalReducedData(reducedData);
                setLoading(false);
            } catch (error) {
                setLoading(false);

                console.error("Error fetching predictions:", error);
            } finally {
                setLoading(false);
            }

        };

        if (from && to && setpoints?.length > 0) {
            getPredictions();
        }
    }, [from, to, selectedSetpoints, sensitivity]);

    const processPredictionData = (data) => {
        const series = [];
        const colors = [
            "#003f5c", "#2f4b7c", "#665191", "#a05195", "#d45087",
            "#f95d6a", "#ff7c43", "#ffa600", "#4f81bd", "#6fa3ef",
            "#9a8c98", "#f0b7a4", "#f3c6b8", "#8b9dc3", "#96d38c",
            "#ffb84c", "#a6c46d", "#a1525b", "#b08e6e", "#6c5b7b",
            "#b07d68", "#7b4e56", "#a7c5bd", "#c2a8c6", "#c9d3d3"
        ];

        const colors2 = [
            // Group 1: Blues & Purples (Cool Tones)
            "#003f5c", "#2f4b7c", "#3b5998", "#4a69bd", "#5d5fef",
            "#665191", "#7161ef", "#8b5cf6", "#a05195", "#ae60e6",
            "#b185db", "#c39bd3", "#d0aaff", "#d45087", "#e37baa",
            "#f0a6ca", "#ff80bf", "#ff66a3", "#ff4d94", "#ff3385",
            "#8c52ff", "#7a5199", "#6a0572", "#580aff", "#440381",

            // Group 2: Reds, Oranges & Yellows (Warm & High-Visibility Tones)
            "#8b0000", "#b22222", "#dc143c", "#e63946", "#f25f5c",
            "#ff4500", "#ff6347", "#ff7f50", "#ff8c00", "#ffa500",
            "#ffb84c", "#ffcc00", "#ffc857", "#ffd700", "#ffdb58",
            "#f3c6b8", "#f0b7a4", "#e6a400", "#e6b800", "#ffcc80",
            "#ffbf69", "#ffa600", "#ff8f00", "#ff7518", "#e86a33",
            "#f97300", "#ff5f00", "#cc5500", "#b7410e", "#a45a52",

            // Group 3: Greens & Teals (Natural & Calm Tones)
            "#004d40", "#00695c", "#00897b", "#00a896", "#00bfa5",
            "#26a69a", "#2a9d8f", "#4caf50", "#52b788", "#6fa3ef",
            "#7bc043", "#8b9dc3", "#96d38c", "#a6c46d", "#b0c4de",
            "#a7c5bd", "#a0c4a5", "#87c38f", "#70a288", "#5a9367",
            "#418f89", "#339989", "#228b22", "#198754", "#137547",
            "#157a6e", "#1b998b", "#2c786c", "#358856", "#4aa96c",

            // Group 4: Earthy & Neutral Tones (Muted & Background-Friendly)
            "#3e2723", "#4e342e", "#5d4037", "#6d4c41", "#795548",
            "#8d6e63", "#a1525b", "#b08e6e", "#b07d68", "#c2a8c6",
            "#c9d3d3", "#d2b48c", "#e0ac69", "#e1c16e", "#f5deb3",
            "#e6ccb2", "#d4a373", "#c9a27c", "#b7a57a", "#a79b89",
            "#9a8c98", "#8b6f68", "#775e5e", "#6c5b7b", "#5e5a5a",
            "#847f72", "#b3a394", "#d4c5b0", "#b8a394", "#91877c"
        ];
        
        const deviceIds = Object.keys(data);
        const colorPalette = deviceIds.length === 1 ? colors : colors2;
        let spotPricesSeries = [];
        if(Object.keys(spotPrices).length > 0){
            spotPricesSeries = spotPrices.electricity_day_ahead_prices.map((price) => ({
                x: new Date(price.datetime).getTime(),
                y: price.value,
            }));
        }
        
        deviceIds.forEach((deviceId, deviceIndex) => {
            const predictions = data[deviceId];
            let colorIndex = deviceIndex * 25; // Each device gets its own 25-color segment

            Object.entries(predictions).forEach(([predictionTime, values]) => {
                if (values.length > 0) {
                    series.push({
                        name: predictionTime,
                        data: values.map((v) => ({
                            x: new Date(v.datetime).getTime(),
                            y: v.value
                        })),
                        color: colorPalette[colorIndex % colorPalette.length]
                    });
                    colorIndex++;
                }
            });
        });

        series.push({name: "spotPrices", data: spotPricesSeries,color: "#000000"});
        return { series };
    };

    useEffect(() => {
        if (finalReducedData && Object.keys(finalReducedData).length > 0) {
            const chartData = processPredictionData(finalReducedData);
            setChartData(chartData);
        }
    }, [finalReducedData, spotPrices]);

    const options = {
        chart: {
            type: "line",
            height: 450,
            zoom: {
                enabled: true, type: 'xy',
                autoScaleYaxis: false,
                allowMouseWheelZoom: false,
                zoomedArea: {
                    fill: {
                        color: '#90CAF9',
                        opacity: 0.4
                    },
                    stroke: {
                        color: '#0D47A1',
                        opacity: 0.4,
                        width: 1
                    }
                }
            },
            toolbar: { show: true },
            animations: {
                enabled: false
            },
        },
        xaxis: {
            type: "datetime",
            labels: {
                datetimeUTC: false,
                format: 'dd/MM/yyyy HH:mm',
                rotate: -45,  // Add this line to rotate labels

            },
            /* labels: {
                datetimeUTC: false,  // This makes it use local time
                format: 'HH:mm',     // Show hours and minutes
                style: {
                    fontSize: '12px'
                }
            }, */
            tooltip: {
                formatter: function (val) {
                    return new Date(val).toLocaleString(); // Local time in tooltip
                }
            }
        },
        yaxis: {
            title: { text: "Setpoint Prediction Values" }
        },
        annotations: {
            xaxis: [{
                x: new Date().getTime(), // Current time
                borderColor: '#483F5E',
                label: {
                    style: {
                        color: '#fff',
                        background: '#483F5E'
                    },
                    text: new Date().toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                    })
                },
                strokeDashArray: 0,
                borderWidth: 2,
            }]
        },
        tooltip: {
            enabled: true,
            followCursor: true,
            intersect: false,
            shared: true,
            fixed: {
                enabled: true,      // Enable fixed position
                position: 'topRight', // Position the tooltip
                offsetX: 10,        // Add some horizontal offset
                offsetY: 10,        // Add some vertical offset
            },
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                let tooltipContent = '<div class="custom-tooltip2">';

                // Find the exact timestamp for this point
                const timestamp = new Date(w.globals.seriesX[seriesIndex][dataPointIndex]);
                const timeStr = timestamp.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
                tooltipContent += `<div style="padding: 5px; border-bottom: 1px solid #eee;">
                    <b>${timeStr}</b>
                </div>`;
                // Get all values for this exact timestamp
                const exactTime = timestamp.getTime();
                let predictions = w.globals.seriesNames
                    .map((seriesName, index) => {
                        const dataIndex = w.globals.seriesX[index].findIndex(x => x === exactTime);
                        if (dataIndex !== -1) {
                            return {
                                name: seriesName,
                                value: series[index][dataIndex]
                            };
                        }
                        return null;
                    })
                    .filter(pred => pred !== null)
                    // Sort by prediction time (latest first)
                    .sort((a, b) => b.name.localeCompare(a.name));

                // Add predictions to tooltip
                predictions.forEach((pred, index) => {
                    tooltipContent += `
                        <div style="margin: 10px; padding: 5px; display: flex; justify-content: space-between; 
                            ${index % 2 === 0 ? 'background-color: #ffffff;' : 'background-color: #f8f9fa;'}">
                            <span style="margin-right: 10px;">${pred.name}:</span>
                            <span style="font-weight: bold;">${pred.value.toFixed(2)}</span>
                        </div>`;
                });

                tooltipContent += '</div>';
                return tooltipContent;
            },
        },
        stroke: {
            width: 2,
            curve: "stepline"
        },
        markers: {
            size: 4
        },
        legend: {
            show: true,
            position: 'top',
            horizontalAlign: 'left',
        }
    };


    const removeSelectedOption = (option) => {
        setSelectedOptions(selectedOptions.filter(item => item !== option));
    }

    const handleApply = () => {
        function filterDataByPredictionNames(data, selectedPredictions) {
            const filteredData = {};

            Object.entries(data).forEach(([setpoint, predictions]) => {
                const filteredPredictions = Object.entries(predictions)
                    .filter(([key, value]) => selectedPredictions.includes(key))  // Adları seçilen array ile karşılaştır
                    .reduce((acc, [key, value]) => {
                        acc[key] = value;  // Seçilen prediction'ları ekle
                        return acc;
                    }, {});

                if (Object.keys(filteredPredictions).length > 0) {
                    filteredData[setpoint] = filteredPredictions;
                }
            });

            return filteredData;
        }
        const result = filterDataByPredictionNames(finalReducedData, selectedOptions);
        setFinalReducedData(result);
        setOpenOptionList(false);
    }
    const handleClose = () => {
        setOpenOptionList(false);
    }

    return (
        <Box sx={{
            width: "100%",
            height: "100%",
            p: 2,
            backgroundColor: "#fff",
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            marginBottom: 20
        }}>
            <Typography sx={{ justifyContent: "center", display: "flex", mb: 2 }} variant="h5">Setpoint Prediction Graph</Typography>

            <TextField sx={{
                width: 200,
                margin: "10px",
                color: "white",
            }} size="small" label="Sensitivity" value={sensitivity} onChange={(e) => setSensitivity(e.target.value)} />

            <Dialog open={openOptionList} onClose={() => setOpenOptionList(false)}>
                <DialogTitle>Select Columns</DialogTitle>
                <DialogContent>
                    {allPredsNames.map((predname) => (
                        <Button key={predname} sx={selectedOptions.includes(predname) ? {
                            margin: "10px",
                            backgroundColor: "#1F9562",
                            color: "white", "&:hover": { backgroundColor: "#1F956280" }
                        } :
                            {
                                margin: "10px", backgroundColor: "#808080", color: "white",
                                "&:hover": { backgroundColor: "#80808080" }
                            }}
                            onClick={() => selectedOptions.includes(predname) ? removeSelectedOption(predname) : setSelectedOptions([...selectedOptions, predname])}>{predname}</Button>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleClose()}>Close</Button>
                    <Button onClick={() => handleApply()}>Apply</Button>
                </DialogActions>
            </Dialog>
            {setpoints.map((setpoint) => (
                <Button
                    key={setpoint}
                    style={{
                        backgroundColor: selectedSetpoints.includes(setpoint) ? "#1F9562" : "gray",
                        color: "white",
                        textTransform: "uppercase",
                        width: "160px",
                        borderRadius: "10px",
                        margin: "10px",
                        color: "white",
                        padding: "10px",
                        fontSize: "10px",
                    }}
                    onClick={() => setSelectedSetpoints(selectedSetpoints.includes(setpoint) ?
                        selectedSetpoints.filter(s => s !== setpoint) :
                        [...selectedSetpoints, setpoint]
                    )}>
                    {setpoint}
                </Button>
            ))}
            <Button onClick={() => setOpenOptionList(!openOptionList)}><MoreHorizIcon fontSize="medium" /></Button>

            <Box sx={{ marginTop: 5 }}>
                {Object.keys(chartData).length > 0 && (
                    <ReactApexChart

                        options={options}
                        series={chartData.series}
                        type="line"
                        height={450}
                    />
                )}
            </Box>
            {savedTooltipsSetpoints.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Saved Setpoint Tooltips ({savedTooltipsSetpoints.length})
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        gap: 2,
                        pb: 2,
                        '::-webkit-scrollbar': {
                            height: '8px',
                        },
                        '::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '4px',
                        },
                        '::-webkit-scrollbar-thumb': {
                            background: '#888',
                            borderRadius: '4px',
                            '&:hover': {
                                background: '#555',
                            },
                        },
                    }}>
                        {savedTooltipsSetpoints.map((tooltip, index) => (
                            <Box
                                key={tooltip.id}
                                sx={{
                                    position: 'relative',
                                    border: '1px solid rgba(0,0,0,0.1)',
                                    borderRadius: 2,
                                    backgroundColor: tooltip.backgroundColor,
                                    p: 2,
                                    minWidth: '300px',
                                    flex: '0 0 auto',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    }
                                }}
                            >

                                <IconButton
                                    onClick={() => handleRemoveTooltip(tooltip.id)}
                                    sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: 8,
                                        zIndex: 2,
                                        backgroundColor: 'rgba(255,255,255,0.8)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.95)',
                                        }
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                                <div
                                    dangerouslySetInnerHTML={{ __html: tooltip.content }}
                                    style={{
                                        position: 'static',
                                        transform: 'none',
                                        marginLeft: 0,
                                        marginTop: 0,
                                        paddingTop: '30px', // Add space for the number
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

